import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import apiService from '../services/api';
import Logger from '../utils/logger';
import Logo from '../components/Logo';
import { Button, Card, Input, Spinner } from '../components/ui';
import { McpSecretEntry } from '../types/api';
import { getApiErrorMessage } from '../constants/messages';

/**
 * La saisie d'un secret demandee par un agent IA.
 *
 * C'est la moitie visible de l'elicitation en mode URL (plan MCP, palier 2, C13).
 * L'agent n'a envoye que des NOMS ; les valeurs se tapent ici, dans une session
 * authentifiee, et partent directement a l'API. Elles ne repassent jamais par le
 * serveur MCP ni par la conversation avec l'agent.
 *
 * L'API rend le meme 404 qu'une demande introuvable si le compte connecte n'est pas
 * celui pour qui elle a ete emise : une adresse recopiee dans un canal partage ne donne
 * donc rien.
 */
const McpSecretEntryPage: React.FC = () => {
  // @ts-ignore -- TS2589: useTranslation type instantiation too deep
  const { t } = (useTranslation as any)('organisations');
  const { entryId } = useParams<{ entryId: string }>();

  const [entry, setEntry] = useState<McpSecretEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [valeurs, setValeurs] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const charger = useCallback(async () => {
    if (!entryId) return;
    setLoading(true);
    try {
      const r = await apiService.getMcpSecretEntry(entryId);
      setEntry(r);
      setDone(r.applique);
      setError(null);
    } catch (err: unknown) {
      Logger.error('MCP secret entry load failed', err);
      setError(t('mcpSecretEntry.notFound'));
    } finally {
      setLoading(false);
    }
  }, [entryId, t]);

  useEffect(() => {
    charger();
  }, [charger]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!entryId) return;
    const remplies = Object.fromEntries(
      Object.entries(valeurs).filter(([, v]) => v.trim() !== ''),
    );
    if (Object.keys(remplies).length === 0) {
      setError(t('mcpSecretEntry.atLeastOne'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await apiService.submitMcpSecretEntry(entryId, remplies);
      // Les valeurs quittent l'ecran des qu'elles sont parties : rien ne justifie de
      // les garder en memoire de l'onglet.
      setValeurs({});
      setDone(true);
      toast.success(t('mcpSecretEntry.saved'));
    } catch (err: unknown) {
      Logger.error('MCP secret entry submit failed', err);
      setError(getApiErrorMessage(err, t('mcpSecretEntry.saveError')));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-1">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-1 px-4 py-10">
      <div className="w-full max-w-lg">
        <div className="flex justify-center mb-6">
          <Logo />
        </div>
        <Card>
          <Card.Header>
            <h1 className="text-[15px] font-semibold text-text-primary">
              {t('mcpSecretEntry.title')}
            </h1>
          </Card.Header>
          <Card.Body>
            {!entry ? (
              <p className="text-[12px] text-text-secondary">
                {error || t('mcpSecretEntry.notFound')}
              </p>
            ) : done ? (
              <div className="space-y-3">
                <p className="text-[12px] text-text-primary">
                  {t('mcpSecretEntry.doneTitle', { application: entry.application })}
                </p>
                <p className="text-[12px] text-text-secondary">
                  {t('mcpSecretEntry.doneHint')}
                </p>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-4">
                <p className="text-[12px] text-text-secondary">
                  {t('mcpSecretEntry.intro', { application: entry.application })}
                </p>
                <p className="text-[12px] text-text-tertiary">
                  {t('mcpSecretEntry.neverSeen')}
                </p>
                {entry.noms_demandes.map((nom) => (
                  <Input
                    key={nom}
                    type="password"
                    label={nom}
                    autoComplete="new-password"
                    value={valeurs[nom] || ''}
                    onChange={(e) => setValeurs((v) => ({ ...v, [nom]: e.target.value }))}
                    placeholder={t('mcpSecretEntry.valuePlaceholder') as string}
                  />
                ))}
                {error && <p className="text-[12px] text-danger">{error}</p>}
                <Button type="submit" disabled={submitting} className="w-full">
                  {submitting ? t('mcpSecretEntry.saving') : t('mcpSecretEntry.submit')}
                </Button>
              </form>
            )}
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default McpSecretEntryPage;
